import {
  faInfoCircle,
  faTachometerAlt
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Badge, Card, Container, Grid, Group, NavLink, Stack } from "@mantine/core";
import React from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { IState } from "../../data/reducers/state";
import { IAssociation } from "../../types/association/Association";
import { IPerson } from "../../types/persons/person";
import { Privilege } from "../../types/persons/privilege";
import { Role } from "../../types/persons/Role";
import AssociationSelector from "../management/Association/AssociationSelector";

// Import all admin components
import ActivitiesPage from "../activities/ActivitiesPage";
import { AvailabilitiesOrgDashboard } from "../availabilities/AvailabilitiesOrgDashboard";
import ImportsPage from "../imports/ImportsPage";
import InvoiceDashboard from "../invoice/InvoiceDashboard";
import ShiftManager from "../management/Shifts/ShiftManager";
import PayrateManagement from "../payrates/PayrateManagement";
import PersonsViewer from "../person/personManager/PersonsViewer";
import { QueueManager } from "../queue/QueueManager";
import ReportsList from "../reports/ReportsList";
import TimeSheetAdmin from "../timesheet/TimeSheetAdmin";
import AdminOverview from "./AdminOverview";
import CommunicationsViewer from "./communications/CommunicationsViewer";
import FinancePage from "./finance/FinancePage";
import AssociationResourcesManager from "./resources/AssociationResourcesManager";
import TeamSearchPage from "./TeamSearchPage";
import UnconfirmedAppointmentsPage from "./UnconfirmedAppointmentsPage";

interface AdminDashboardProps {
  // Redux props
  self: IPerson;
  privilege: Privilege;
  role: Role;
  associations: IAssociation[];
}

interface AdminTab {
  id: string;
  title: string;
  description: string;
  icon: string;
  path: string;
  disabled?: boolean;
  badge?: string;
}

/**
 * Admin dashboard with GitHub-style sidebar navigation
 * Provides tabbed interface for all administrative functions
 */
const AdminDashboard: React.FunctionComponent<AdminDashboardProps> = ({
  self,
  privilege,
  role,
  associations,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Extract current tab from pathname
  const pathParts = location.pathname.split('/');
  const currentTab = pathParts[2] || 'overview'; // /admin/[tab]

  // Association management
  const [selectedAssociation, setSelectedAssociation] = React.useState<IAssociation | null>(null);

  // Initialize association
  React.useEffect(() => {
    if (associations && associations.length > 0 && !selectedAssociation) {
      setSelectedAssociation(associations[0]);
    }
  }, [associations, selectedAssociation]);

  // Sync selected association with updated data from Redux
  React.useEffect(() => {
    if (selectedAssociation && associations) {
      const updatedAssociation = associations.find(a => a._id === selectedAssociation._id);
      if (updatedAssociation && JSON.stringify(updatedAssociation.config) !== JSON.stringify(selectedAssociation.config)) {
        setSelectedAssociation(updatedAssociation);
      }
    }
  }, [associations, selectedAssociation]);

  React.useEffect(() => {
    document.title = "Stadium Supervisor - Admin Dashboard";
  }, []);

  const isManager = Privilege.hasPrivilege(privilege, Privilege.manager);
  const isShiftSupervisor = Privilege.hasPrivilege(privilege, Privilege.shiftSupervisor);
  const isSystemAdmin = self?.admin || false;
  const isSupervisor = Role.hasRole(role, Role.supervisor);

  // Different permission levels
  const canManageFinancials = isManager || isSystemAdmin;
  const canManageShifts = isManager || isShiftSupervisor || isSystemAdmin;
  const canManagePeople = isManager || isShiftSupervisor || isSystemAdmin;

  // Define admin tabs organized by category
  const adminTabsByCategory: Record<string, AdminTab[]> = {
    people: [
      {
        id: 'people',
        title: 'People',
        description: 'Manage referees, officials & their details',
        icon: 'fas fa-users',
        path: '/admin/people',
        disabled: !canManagePeople,
      },
      {
        id: 'availabilities',
        title: 'Availabilities',
        description: 'Organization-wide availability management',
        icon: 'fas fa-calendar-check',
        path: '/admin/availabilities',
        disabled: !canManageShifts,
      },
      {
        id: 'resources',
        title: 'Resources',
        description: 'Manage association resources & announcements',
        icon: 'fas fa-file-alt',
        path: '/admin/resources',
        disabled: !canManageShifts,
      },
    ],
    rostering: [
      {
        id: 'shifts',
        title: 'Shifts',
        description: 'Create and manage shifts & sessions',
        icon: 'fas fa-calendar-alt',
        path: '/admin/shifts',
        disabled: !(isSupervisor || canManageShifts),
      },
      {
        id: 'unconfirmed',
        title: 'Unconfirmed',
        description: 'View published appointments awaiting confirmation',
        icon: 'fas fa-exclamation-circle',
        path: '/admin/unconfirmed',
        badge: "New",
        disabled: !canManageShifts,
      },
      {
        id: 'imports',
        title: 'Game Imports',
        description: 'Import data from external sources',
        icon: 'fas fa-file-import',
        path: '/admin/imports',
        disabled: !canManageShifts,
      },
      {
        id: 'reports',
        title: 'Reports',
        description: 'Generate reports & view analytics',
        icon: 'fas fa-chart-bar',
        path: '/admin/reports',
        disabled: !canManageShifts,
      },
    ],
    pays: [
      {
        id: 'finance',
        title: 'Finance',
        description: 'ABA exports, payments & financial reporting',
        icon: 'fas fa-money-check-alt',
        path: '/admin/finance',
        disabled: !canManageFinancials,
      },
      {
        id: 'payrates',
        title: 'Pay Rates',
        description: 'Create and manage association payrates',
        icon: 'fas fa-dollar-sign',
        path: '/admin/payrates',
        disabled: !canManageFinancials,
      },
      {
        id: 'invoices',
        title: 'Invoices',
        description: 'Manage invoices & billing',
        icon: 'fas fa-file-invoice',
        path: '/admin/invoices',
        disabled: !canManageFinancials,
      },
      {
        id: 'timesheet',
        title: 'Timesheets',
        description: 'Legacy timesheet management',
        icon: 'fas fa-clock',
        path: '/admin/timesheet',
        disabled: !canManageFinancials,
        badge: 'Legacy',
      },
    ],
    admin: [
      {
        id: 'overview',
        title: 'Configuration',
        description: 'Dashboard overview and quick stats',
        icon: 'fas fa-tachometer-alt',
        path: '/admin/overview',
        disabled: !isSystemAdmin,
      },
      {
        id: 'communications',
        title: 'Communications',
        description: 'View all sent communications and emails',
        icon: 'fas fa-envelope',
        path: '/admin/communications',
        disabled: !isSystemAdmin,
      },
      {
        id: 'teams',
        title: 'Teams',
        description: 'Search and filter teams by name, games, and schedule',
        icon: 'fas fa-users-cog',
        path: '/admin/teams',
        disabled: !isSystemAdmin,
      },
      {
        id: 'activities',
        title: 'Activities',
        description: 'Configure activities & event types',
        icon: 'fas fa-clipboard-list',
        path: '/admin/activities',
        disabled: !isSystemAdmin,
      },
      {
        id: 'queue',
        title: 'Queue',
        description: 'Background jobs & processing queue',
        icon: 'fas fa-tasks',
        path: '/admin/queue',
        disabled: true,
      },
    ],
  };

  // Flatten all tabs for compatibility with existing code
  const adminTabs: AdminTab[] = [
    ...adminTabsByCategory.people,
    ...adminTabsByCategory.rostering,
    ...adminTabsByCategory.pays,
    ...adminTabsByCategory.admin,
  ];

  const handleTabClick = (e: React.MouseEvent, tabId: string, disabled?: boolean) => {
    if (disabled) return;

    // Allow default behavior for command/ctrl+click and right-click
    if (e.ctrlKey || e.metaKey || e.button === 2) {
      return;
    }

    // Only prevent default for regular left clicks
    e.preventDefault();
    navigate(`/admin/${tabId}`);
  };

  const enabledTabs = adminTabs.filter(tab => !tab.disabled);
  const disabledTabs = adminTabs.filter(tab => tab.disabled);
  const activeTab = adminTabs.find(tab => tab.id === currentTab);

  // Direct component rendering based on current tab
  const renderAdminPage = () => {
    // Some pages can work without an association (like shifts, payrates, invoices, communications, unconfirmed)
    const requiresAssociation = !['shifts', 'payrates', 'invoices', 'communications', 'unconfirmed'].includes(currentTab);
    if (requiresAssociation && !selectedAssociation) return null;

    switch (currentTab) {
      case 'overview':
        return <AdminOverview selectedAssociation={selectedAssociation} associations={associations} />;
      case 'unconfirmed':
        return <UnconfirmedAppointmentsPage selectedAssociation={selectedAssociation} />;
      case 'finance':
        return <FinancePage selectedAssociation={selectedAssociation} associations={associations} />;
      case 'communications':
        return <CommunicationsViewer />;
      case 'people':
        return <PersonsViewer defaultAssociationId={selectedAssociation?._id} />;
      case 'teams':
        return <TeamSearchPage selectedAssociation={selectedAssociation} />;
      case 'shifts':
        return <ShiftManager selectedAssociation={selectedAssociation} {...{} as any} />;
      case 'imports':
        // Connected component - let Redux handle the props
        return <ImportsPage selectedAssociation={selectedAssociation} />;
      case 'activities':
        // Connected component - let Redux handle the props  
        return <ActivitiesPage />;
      case 'reports':
        return <ReportsList selectedAssociation={selectedAssociation} />;
      case 'payrates':
        // Connected component - let Redux handle the props
        return <PayrateManagement selectedAssociation={selectedAssociation as any} />;
      case 'invoices':
        return <InvoiceDashboard selectedAssociation={selectedAssociation} />;
      case 'timesheet':
        // Connected component - let Redux handle the props
        return <TimeSheetAdmin selectedAssociation={selectedAssociation} />;
      case 'availabilities':
        return <AvailabilitiesOrgDashboard association={selectedAssociation} />;
      case 'resources':
        return <AssociationResourcesManager selectedAssociation={selectedAssociation} />;
      case 'queue':
        return <QueueManager selectedAssociation={selectedAssociation as any} />;
      default:
        return <AdminOverview selectedAssociation={selectedAssociation} associations={associations} />;
    }
  };

  return (
    <Container fluid py="sm">
      <Grid>
        {/* Sidebar Navigation */}
        <Grid.Col
          span={{ base: 12, sm: 4, md: 3, lg: 2 }}
          mb="sm"
        >
          <Card
            shadow="sm"
            padding="sm"
            radius="md"
            withBorder
            style={{
              position: 'sticky',
              top: '20px',
              backgroundColor: 'var(--color-admin-sidebar)',
            }}
          >
            <Stack gap="md">
              {/* Header */}
              <div
                style={{
                  padding: '16px',
                  backgroundColor: 'var(--color-admin-elevated)',
                  borderRadius: '8px',
                  border: '1px solid var(--color-table-border)'
                }}
              >
                <Group gap="sm" mb="xs">
                  <FontAwesomeIcon
                    icon={faTachometerAlt}
                    style={{
                      color: 'var(--color-button-primary)',
                      fontSize: '18px'
                    }}
                  />
                  <h6 style={{ margin: 0, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                    Admin Dashboard
                  </h6>
                </Group>
                <small style={{ color: 'var(--color-text-medium)' }}>
                  Welcome, {self?.preferredName || self?.firstName}
                </small>
              </div>

              {/* Navigation Categories */}
              <div>
                <Stack gap="lg">
                  {/* People Category */}
                  {adminTabsByCategory.people.some(tab => !tab.disabled) && (
                    <div>
                      <small
                        style={{
                          textTransform: 'uppercase',
                          color: 'var(--color-text-disabled)',
                          fontWeight: 700,
                          fontSize: '11px',
                          letterSpacing: '0.5px'
                        }}
                      >
                        People
                      </small>
                      <Stack gap="xs" mt="sm">
                        {adminTabsByCategory.people.filter(tab => !tab.disabled).map((tab) => (
                          <NavLink
                            key={tab.id}
                            href={tab.path}
                            active={currentTab === tab.id}
                            onClick={(e) => handleTabClick(e, tab.id)}
                            leftSection={
                              <i
                                className={tab.icon}
                                style={{
                                  width: '16px',
                                  fontSize: '14px',
                                  color: currentTab === tab.id
                                    ? 'var(--color-button-primary)'
                                    : 'var(--color-text-medium)'
                                }}
                              />
                            }
                            rightSection={tab.badge && (
                              <Badge
                                color={tab.badge === 'New' ? 'blue' : 'gray'}
                                size="xs"
                                variant="light"
                              >
                                {tab.badge}
                              </Badge>
                            )}
                            styles={{
                              root: {
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: currentTab === tab.id ? 600 : 500,
                                padding: '8px 12px',
                                color: currentTab === tab.id
                                  ? 'var(--color-button-primary)'
                                  : 'var(--color-text-primary)',
                                backgroundColor: currentTab === tab.id
                                  ? 'var(--color-admin-sidebar-active)'
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: 'var(--color-admin-sidebar-hover)',
                                  color: 'var(--color-text-primary)'
                                }
                              },
                              label: {
                                fontSize: '14px',
                                color: 'inherit'
                              }
                            }}
                            label={tab.title}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}

                  {/* Rostering Category */}
                  {adminTabsByCategory.rostering.some(tab => !tab.disabled) && (
                    <div>
                      <small
                        style={{
                          textTransform: 'uppercase',
                          color: 'var(--color-text-disabled)',
                          fontWeight: 700,
                          fontSize: '11px',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Rostering
                      </small>
                      <Stack gap="xs" mt="sm">
                        {adminTabsByCategory.rostering.filter(tab => !tab.disabled).map((tab) => (
                          <NavLink
                            key={tab.id}
                            href={tab.path}
                            active={currentTab === tab.id}
                            onClick={(e) => handleTabClick(e, tab.id)}
                            leftSection={
                              <i
                                className={tab.icon}
                                style={{
                                  width: '16px',
                                  fontSize: '14px',
                                  color: currentTab === tab.id
                                    ? 'var(--color-button-primary)'
                                    : 'var(--color-text-medium)'
                                }}
                              />
                            }
                            rightSection={tab.badge && (
                              <Badge
                                color={tab.badge === 'New' ? 'blue' : 'gray'}
                                size="xs"
                                variant="light"
                              >
                                {tab.badge}
                              </Badge>
                            )}
                            styles={{
                              root: {
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: currentTab === tab.id ? 600 : 500,
                                padding: '8px 12px',
                                color: currentTab === tab.id
                                  ? 'var(--color-button-primary)'
                                  : 'var(--color-text-primary)',
                                backgroundColor: currentTab === tab.id
                                  ? 'var(--color-admin-sidebar-active)'
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: 'var(--color-admin-sidebar-hover)',
                                  color: 'var(--color-text-primary)'
                                }
                              },
                              label: {
                                fontSize: '14px',
                                color: 'inherit'
                              }
                            }}
                            label={tab.title}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}

                  {/* Pays Category */}
                  {adminTabsByCategory.pays.some(tab => !tab.disabled) && (
                    <div>
                      <small
                        style={{
                          textTransform: 'uppercase',
                          color: 'var(--color-text-disabled)',
                          fontWeight: 700,
                          fontSize: '11px',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Pays
                      </small>
                      <Stack gap="xs" mt="sm">
                        {adminTabsByCategory.pays.filter(tab => !tab.disabled).map((tab) => (
                          <NavLink
                            key={tab.id}
                            href={tab.path}
                            active={currentTab === tab.id}
                            onClick={(e) => handleTabClick(e, tab.id)}
                            leftSection={
                              <i
                                className={tab.icon}
                                style={{
                                  width: '16px',
                                  fontSize: '14px',
                                  color: currentTab === tab.id
                                    ? 'var(--color-button-primary)'
                                    : 'var(--color-text-medium)'
                                }}
                              />
                            }
                            rightSection={tab.badge && (
                              <Badge
                                color={tab.badge === 'New' ? 'blue' : 'gray'}
                                size="xs"
                                variant="light"
                              >
                                {tab.badge}
                              </Badge>
                            )}
                            styles={{
                              root: {
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: currentTab === tab.id ? 600 : 500,
                                padding: '8px 12px',
                                color: currentTab === tab.id
                                  ? 'var(--color-button-primary)'
                                  : 'var(--color-text-primary)',
                                backgroundColor: currentTab === tab.id
                                  ? 'var(--color-admin-sidebar-active)'
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: 'var(--color-admin-sidebar-hover)',
                                  color: 'var(--color-text-primary)'
                                }
                              },
                              label: {
                                fontSize: '14px',
                                color: 'inherit'
                              }
                            }}
                            label={tab.title}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}

                  {/* Admin Category */}
                  {adminTabsByCategory.admin.some(tab => !tab.disabled) && (
                    <div>
                      <small
                        style={{
                          textTransform: 'uppercase',
                          color: 'var(--color-text-disabled)',
                          fontWeight: 700,
                          fontSize: '11px',
                          letterSpacing: '0.5px'
                        }}
                      >
                        Admin
                      </small>
                      <Stack gap="xs" mt="sm">
                        {adminTabsByCategory.admin.filter(tab => !tab.disabled).map((tab) => (
                          <NavLink
                            key={tab.id}
                            href={tab.path}
                            active={currentTab === tab.id}
                            onClick={(e) => handleTabClick(e, tab.id)}
                            leftSection={
                              <i
                                className={tab.icon}
                                style={{
                                  width: '16px',
                                  fontSize: '14px',
                                  color: currentTab === tab.id
                                    ? 'var(--color-button-primary)'
                                    : 'var(--color-text-medium)'
                                }}
                              />
                            }
                            rightSection={tab.badge && (
                              <Badge
                                color={tab.badge === 'New' ? 'blue' : 'gray'}
                                size="xs"
                                variant="light"
                              >
                                {tab.badge}
                              </Badge>
                            )}
                            styles={{
                              root: {
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: currentTab === tab.id ? 600 : 500,
                                padding: '8px 12px',
                                color: currentTab === tab.id
                                  ? 'var(--color-button-primary)'
                                  : 'var(--color-text-primary)',
                                backgroundColor: currentTab === tab.id
                                  ? 'var(--color-admin-sidebar-active)'
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: 'var(--color-admin-sidebar-hover)',
                                  color: 'var(--color-text-primary)'
                                }
                              },
                              label: {
                                fontSize: '14px',
                                color: 'inherit'
                              }
                            }}
                            label={tab.title}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}
                </Stack>
              </div>

              {/* User Info */}
              <div
                style={{
                  padding: '16px',
                  backgroundColor: 'var(--color-admin-elevated)',
                  borderRadius: '8px',
                  border: '1px solid var(--color-table-border)'
                }}
              >
                <Stack gap="sm">
                  <div>
                    <small style={{ color: 'var(--color-text-medium)', fontWeight: 600 }}>
                      Permissions
                    </small>
                    <div style={{ fontSize: '13px', marginTop: '2px', color: 'var(--color-text-primary)' }}>
                      {Privilege.getNames(privilege).join(", ")}
                    </div>
                  </div>
                  <div>
                    <small style={{ color: 'var(--color-text-medium)', fontWeight: 600 }}>
                      Roles
                    </small>
                    <div style={{ fontSize: '13px', marginTop: '2px', color: 'var(--color-text-primary)' }}>
                      {Role.getRoleNames(role).join(", ")}
                    </div>
                  </div>
                </Stack>
              </div>
            </Stack>
          </Card>
        </Grid.Col>

        {/* Main Content Area */}
        <Grid.Col span={{ base: 12, sm: 8, md: 9, lg: 10 }} style={{ flex: '1' }}>
          <Stack gap="md">
            {/* Association Selection Bar */}
            {associations && associations.length > 1 && (
              <Alert color="blue" variant="light" radius="md">
                <Grid align="center">
                  <Grid.Col span={{ base: 12, md: 8 }}>
                    <Group>
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        style={{ color: 'var(--mantine-color-blue-6)' }}
                      />
                      <div>
                        <strong>Association Selection</strong>
                        <div style={{ fontSize: '14px', marginTop: '2px' }}>
                          You have access to {associations.length} associations.
                          Admin operations will apply to the selected association.
                        </div>
                      </div>
                    </Group>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <AssociationSelector
                      value={selectedAssociation?._id}
                      associations={associations}
                      onChange={setSelectedAssociation}
                      showLabel={false}
                    />
                  </Grid.Col>
                </Grid>
              </Alert>
            )}

            {/* Tab Header */}
            {activeTab && (
              <Card
                shadow="sm"
                padding="md"
                radius="md"
                withBorder
                style={{}}
              >
                <Group justify="space-between">
                  <Group>
                    <i
                      className={activeTab.icon}
                      style={{
                        fontSize: '24px',
                        color: 'var(--mantine-color-brand-6)'
                      }}
                    />
                    <div>
                      <h4 style={{ margin: 0, marginBottom: '4px', fontWeight: 600 }}>
                        {activeTab.title}
                      </h4>
                      <p style={{
                        margin: 0,
                        fontSize: '14px'
                      }}>
                        {activeTab.description}
                      </p>
                    </div>
                  </Group>
                  {activeTab.badge && (
                    <Badge
                      color={activeTab.badge === 'New' ? 'blue' : 'gray'}
                      variant="light"
                      size="md"
                    >
                      {activeTab.badge}
                    </Badge>
                  )}
                </Group>
              </Card>
            )}

            {/* Tab Content */}
            <div>
              {renderAdminPage()}
            </div>
          </Stack>
        </Grid.Col>
      </Grid>
    </Container>
  );
};


const mapStateToProps = (state: IState) => ({
  self: state.persons.self && state.persons.map[state.persons.self],
  privilege: state.auth.privilege,
  role: state.auth.role,
  associations: state.associations.admin.map(
    (a) => state.associations.map[a],
  ).filter(Boolean), // Filter out any null/undefined associations
});

export default connect(mapStateToProps)(AdminDashboard);
